import { api } from "@/lib/api";
// Per-device guest identity + cabin roster support.
//
// SECURITY: What we store in localStorage here is NON-SENSITIVE UI state —
//   * `yacht_guest_name` — public first-name label the guest just typed in.
//   * `yacht_guest_id` — server-issued opaque guest UUID (not a JWT).
//   * `yacht_cabin_*`  — cabin/charter identifiers the server already knows
//                       about; not authentication.
//   * `yacht_ipad_session` — SHORT device-scoped session token generated by
//                       the backend for the iPad kiosk flow only. Rotates on
//                       every login and is bound to the cabin's iPad. There
//                       is NO admin/JWT auth token in localStorage — those
//                       live in an httpOnly cookie set by `/api/auth/login`.
//   * `yacht_ipad_mode` — boolean "1" flag for the iPad UI mode.
//
// Persists in localStorage — never expires until "Sign out" is pressed (iPad-friendly).
const NAME_KEY = "yacht_guest_name";
const ID_KEY = "yacht_guest_id";
const CABIN_KEY = "yacht_guest_cabin";
const ROSTER_KEY = "yacht_cabin_roster";        // JSON array of {guest_id, guest_name, partner_guest_id}
const CABIN_ID_KEY = "yacht_cabin_id";
const CHARTER_ID_KEY = "yacht_charter_id";
const CHARTER_ARRIVAL_KEY = "yacht_charter_arrival";
const CHARTER_DEPARTURE_KEY = "yacht_charter_departure";
const FEMALE_KEY = "yacht_guest_female";        // "1" when the active guest is female — drives the pregnancy question in the wellness dialog
const IPAD_TOKEN_KEY = "yacht_ipad_session";    // long-lived iPad session token
const IPAD_MODE_KEY = "yacht_ipad_mode";        // "1" if signed in via iPad flow
const IPAD_COVER_KEY = "yacht_ipad_cover";      // path of the cabin cover photo — used to paint every iPad screen with the masked hero

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "g_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getGuest() {
  let roster = [];
  try { roster = JSON.parse(localStorage.getItem(ROSTER_KEY) || "[]"); } catch { roster = []; }
  // Backward-compat: existing signed-in guests may not have `yacht_guest_female`
  // in localStorage (added iteration 110). If it's missing, resolve it from the
  // cabin roster so the pregnancy row in the wellness dialog surfaces even for
  // guests who signed in before the fix shipped — no re-login required.
  const id = localStorage.getItem(ID_KEY) || null;
  let femaleFlag = localStorage.getItem(FEMALE_KEY);
  if (femaleFlag === null && id) {
    const match = roster.find((r) => r.guest_id === id);
    if (match && typeof match.female === "boolean") {
      femaleFlag = match.female ? "1" : "0";
      try { localStorage.setItem(FEMALE_KEY, femaleFlag); } catch { /* private mode */ }
    }
  }
  return {
    name: localStorage.getItem(NAME_KEY) || null,
    id,
    cabin: localStorage.getItem(CABIN_KEY) || null,
    cabin_id: localStorage.getItem(CABIN_ID_KEY) || null,
    charter_id: localStorage.getItem(CHARTER_ID_KEY) || null,
    charter_arrival: localStorage.getItem(CHARTER_ARRIVAL_KEY) || null,
    charter_departure: localStorage.getItem(CHARTER_DEPARTURE_KEY) || null,
    female: femaleFlag === "1",
    ipad_token: localStorage.getItem(IPAD_TOKEN_KEY) || null,
    ipad_mode: localStorage.getItem(IPAD_MODE_KEY) === "1",
    roster,
  };
}

export function isIpadMode() {
  return localStorage.getItem(IPAD_MODE_KEY) === "1" && !!localStorage.getItem(IPAD_TOKEN_KEY);
}

// Cabin cover photo path (relative — pipe through `fileUrl` at render time).
// Used by the global IpadThemeMount to paint the masked hero on every iPad
// screen without each page having to re-fetch the session. Returns null when
// the operator hasn't uploaded a cover.
export function getIpadCoverPath() {
  return localStorage.getItem(IPAD_COVER_KEY) || null;
}

export function setIpadSession(cabin_id, cabin_name, charter_id, cabin_roster, session_token, charter_arrival, charter_departure, cabin_cover_image_path) {
  if (cabin_id) localStorage.setItem(CABIN_ID_KEY, cabin_id);
  if (cabin_name) localStorage.setItem(CABIN_KEY, cabin_name);
  if (charter_id) localStorage.setItem(CHARTER_ID_KEY, charter_id);
  if (charter_arrival) localStorage.setItem(CHARTER_ARRIVAL_KEY, charter_arrival);
  if (charter_departure) localStorage.setItem(CHARTER_DEPARTURE_KEY, charter_departure);
  if (Array.isArray(cabin_roster)) localStorage.setItem(ROSTER_KEY, JSON.stringify(cabin_roster));
  if (session_token) localStorage.setItem(IPAD_TOKEN_KEY, session_token);
  // Save cover explicitly so the global iPad hero doesn't have to wait for a
  // per-page session refetch. Nullable — empty/null wipes the key so cabins
  // that lose their cover mid-session revert to the neutral background.
  if (cabin_cover_image_path) localStorage.setItem(IPAD_COVER_KEY, cabin_cover_image_path);
  else localStorage.removeItem(IPAD_COVER_KEY);
  // Notify the global <IpadThemeMount /> that the iPad session just changed
  // so the masked background paints on the FIRST login without needing a
  // hard reload. The built-in `storage` event only fires cross-tab, so we
  // ship a custom event for same-tab consumers.
  try { window.dispatchEvent(new Event("yacht-ipad-session-changed")); } catch { /* SSR / very old Safari */ }
  localStorage.setItem(IPAD_MODE_KEY, "1");
  // Clear any previously-active guest so /menu forces a pick.
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(ID_KEY);
}

export function signInGuest(name) {
  const clean = (name || "").trim();
  if (!clean) return null;
  localStorage.setItem(NAME_KEY, clean);
  let id = localStorage.getItem(ID_KEY);
  if (!id) { id = makeId(); localStorage.setItem(ID_KEY, id); }
  return { name: clean, id, cabin: null };
}

export function signInGuestWithSession(payload) {
  const { guest_id, guest_name, female, cabin_name, cabin_id, charter_id, cabin_roster, charter_arrival, charter_departure } = payload || {};
  if (!guest_id || !guest_name) return null;
  localStorage.removeItem(IPAD_TOKEN_KEY);
  localStorage.removeItem(IPAD_MODE_KEY);
  localStorage.setItem(NAME_KEY, guest_name);
  localStorage.setItem(ID_KEY, guest_id);
  localStorage.setItem(FEMALE_KEY, female ? "1" : "0");
  if (cabin_name) localStorage.setItem(CABIN_KEY, cabin_name);
  if (cabin_id) localStorage.setItem(CABIN_ID_KEY, cabin_id);
  if (charter_id) localStorage.setItem(CHARTER_ID_KEY, charter_id);
  if (charter_arrival) localStorage.setItem(CHARTER_ARRIVAL_KEY, charter_arrival);
  if (charter_departure) localStorage.setItem(CHARTER_DEPARTURE_KEY, charter_departure);
  if (Array.isArray(cabin_roster)) localStorage.setItem(ROSTER_KEY, JSON.stringify(cabin_roster));
  return getGuest();
}

export function switchActiveGuest(guest_id) {
  let roster = [];
  try { roster = JSON.parse(localStorage.getItem(ROSTER_KEY) || "[]"); } catch { roster = []; }
  const g = roster.find((r) => r.guest_id === guest_id);
  if (!g) return null;
  localStorage.setItem(NAME_KEY, g.guest_name);
  localStorage.setItem(ID_KEY, g.guest_id);
  // Keep the female flag in sync when the guest switches inside the same cabin
  // so the wellness dialog shows the pregnancy row for the correct occupant.
  localStorage.setItem(FEMALE_KEY, g.female ? "1" : "0");
  return getGuest();
}

export function clearActiveGuest() {
  // Leaves iPad session + roster in place; only "who is currently booking" is cleared.
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(ID_KEY);
  localStorage.removeItem(FEMALE_KEY);
}

export function updateRoster(roster) {
  if (Array.isArray(roster)) localStorage.setItem(ROSTER_KEY, JSON.stringify(roster));
}

export function signOutGuest() {
  api.post("/public/guest-logout").catch(() => {});
  [NAME_KEY, ID_KEY, CABIN_KEY, CABIN_ID_KEY, CHARTER_ID_KEY, CHARTER_ARRIVAL_KEY, CHARTER_DEPARTURE_KEY, FEMALE_KEY, ROSTER_KEY, IPAD_TOKEN_KEY, IPAD_MODE_KEY, IPAD_COVER_KEY].forEach((k) => localStorage.removeItem(k));
  // Broadcast so global mounts (IpadThemeMount, IpadKillMount) drop their
  // signed-in UI immediately instead of waiting for the next reload.
  try { window.dispatchEvent(new Event("yacht-ipad-session-changed")); } catch { /* ignore */ }
}
